import React from 'react'
import PropTypes from 'prop-types'

/**
 * Notification component displays a dismissible toast notification.
 * It automatically dismisses itself after a set timeout or can be closed manually by the user.
 *
 * @module notifications/Notification
 * @description Notification component displays a dismissible toast notification.
 * It automatically dismisses itself after a set timeout or can be closed manually by the user.
 * @param {Object} notification - The notification object containing id, type, and message.
 * @param {string} notification.id - Unique identifier for the notification.
 * @param {string} notification.type - Type of notification (e.g., 'success', 'error', 'info').
 * @param {string} notification.message - The message to display within the notification.
 * @param {number} px - The bottom position in pixels for the notification, to control stacking.
 * @param {function} onDismiss - Function to call when the notification is dismissed.
 *
 * @example
 * <Notification
 *   key={notification.id}
 *   notification={notification}
 *   onDismiss={dismissNotification}
 *   notifications={notifications}
 *   px={px}
 * />
 *
 * @returns {React.Element} The rendered Notification component.
 */
export const Notification = ({ notification: { id, type, message }, px, onDismiss }) => {
  const [openMessage, setOpenMessage] = React.useState(true)

  const closeMessage = () => {
    onDismiss(id)
    setOpenMessage(false)
  }

  React.useEffect(() => {
    const timer1 = setTimeout(() => closeMessage(), 5 * 1000)
    return () => {
      clearTimeout(timer1)
    }
  }, [])

  return (
    <div
      className={`trtui-toastify trtui-bg-${
        type === 'success' ? 'success' : type === 'error' ? 'danger' : 'info'
      } trtui-toastify-right trtui-toastify-bottom`}
      aria-live="polite"
      style={{
        opacity: 1,
        transform: 'translate(0px, 0px)',
        bottom: `${px}px`,
        display: openMessage ? 'block' : 'none',
      }}
    >
      {message}
      <button
        type="button"
        aria-label="Close"
        onClick={() => closeMessage()}
        className="trtui-toast-close"
        data-testid="closeNotification"
      >
        ✖
      </button>
    </div>
  )
}

Notification.propTypes = {
  /**
   * The notification object containing id, type, and message.
   */
  notification: PropTypes.shape({
    /**
     * Unique identifier for the notification.
     */
    id: PropTypes.string.isRequired,

    /**
     * Type of notification (e.g., 'success', 'error', 'info').
     */
    type: PropTypes.string.isRequired,

    /**
     * The message to display within the notification.
     */
    message: PropTypes.string.isRequired,
  }).isRequired,

  /**
   * The bottom position in pixels for the notification, to control stacking.
   */
  px: PropTypes.number.isRequired,

  /**
   * Function to call when the notification is dismissed.
   */
  onDismiss: PropTypes.func.isRequired,
}
